/**
 * IP Geolocation Service
 *
 * Asks the storefront app proxy for a coarse location hint after preferences
 * consent. The proxy returns country, region, and a full postal code. The
 * theme stores only the first 3 characters of that code. The browser never
 * calls a third-party geo API and never sees an access key.
 *
 * Query string overrides (testing):
 * - ?region=BC
 * - ?country=CA
 * - ?zip=V5K0A1  stores only the first 3 characters as prefix
 */

import { CookieManager } from "@theme/cookie-manager";
import { createLogger } from "@theme/logger";
import { LocationLoadedEvent, LocaleMismatchEvent } from "@theme/mysa-events";
import { safeSessionStorage } from "@theme/safe-storage";

const log = createLogger("IPGeolocation");

/** @typedef {Object} StoredLocationData
 * @property {string} country - ISO alpha-2, or ""
 * @property {string} region - Subdivision code, or ""
 * @property {string} prefix - First 3 postal characters, or ""
 */

/**
 * Coarse location hint. Session cookie plus in-memory copy.
 */
class IPGeolocationService {
  /** @type {string} Same-origin app proxy. Shopify signs the request. */
  static #GEO_PATH = "/tools/utility/geo";

  /** @type {number} Proxy request timeout in milliseconds */
  static #TIMEOUT = 5000;

  /** @type {string} Session hint cookie. Not a long-lived postal record. */
  static #COOKIE_NAME = "mysa_location";

  /**
   * Tab flag for a reached 200 that normalized to no country, region, or prefix.
   * Not a cookie. A failed request does not set it.
   */
  static #EMPTY_KEY = "mysa_geo_empty";

  /**
   * @returns {StoredLocationData}
   */
  static #empty() {
    return {
      country: "",
      region: "",
      prefix: "",
    };
  }

  /** @type {StoredLocationData | null} In-memory hint for this document */
  static #memory = null;

  /** @type {boolean} A lookup finished (including an unknown result) */
  static #settled = false;

  /** @type {StoredLocationData} Cached location data */
  #locationData = IPGeolocationService.#empty();

  /** @type {StoredLocationData} Hint taken off the cookie until consent allows it */
  #pendingHint = IPGeolocationService.#empty();

  /** @type {boolean} Whether this document already consumed the stored cookie */
  #hintCaptured = false;

  /** @type {boolean} Whether location response was received */
  #locationReceived = false;

  /** @type {boolean} Whether the consent listener is already registered */
  #listeningForConsent = false;

  /** @type {number} Bumped when a denial drops an in-flight lookup */
  #lookupGeneration = 0;

  /** @type {boolean} Whether a lookup request is waiting on the network */
  #lookupInFlight = false;

  /** @type {AbortController | null} Aborts the lookup that is in flight */
  #abortLookup = null;

  /**
   * Read the cookie once and rewrite a legacy shape in place. Country, region,
   * and prefix stay readable. The cookie is deleted only after an explicit denial.
   */
  #slimStoredHintOnce() {
    if (this.#hintCaptured) return;

    this.#hintCaptured = true;
    const raw = IPGeolocationService.#readRaw();
    this.#pendingHint = raw
      ? IPGeolocationService.#normalize(raw)
      : IPGeolocationService.#empty();

    if (!raw || !IPGeolocationService.#needsRewrite(raw)) return;

    this.#writeSession(this.#pendingHint);
  }

  /**
   * Apply ?region / ?country / ?zip without calling the proxy.
   * Zip overrides store a prefix only.
   * @param {StoredLocationData} existing
   * @returns {boolean} True when an override was applied
   */
  #checkQueryOverrides(existing) {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("region") && !params.has("country") && !params.has("zip")) {
      return false;
    }

    const parsed = IPGeolocationService.#normalize({
      country: params.get("country") || "",
      region: params.get("region") || "",
      postal: params.get("zip") || "",
    });

    if (!IPGeolocationService.#hasHint(parsed)) return false;

    const hint = {
      country:
        parsed.country ||
        existing.country ||
        (parsed.region || parsed.prefix ? "CA" : ""),
      region: parsed.region || existing.region || "",
      prefix: parsed.prefix || existing.prefix || "",
    };

    log("Applying query string overrides", hint);
    this.#publish(hint);
    return true;
  }

  /**
   * Initialize the geolocation service
   */
  async initialize() {
    if (window.Shopify?.designMode) {
      log("Disabled in theme editor");
      return;
    }

    this.#listenForConsent();
    this.#slimStoredHintOnce();

    if (this.#checkQueryOverrides(this.#pendingHint)) return;

    const preferencesAllowed = await this.#preferencesAllowed();

    if (!preferencesAllowed) {
      if (IPGeolocationService.#explicitDenial()) {
        this.#clearStoredLocation();
      }
      return;
    }

    if (this.#locationReceived || this.#lookupInFlight) return;

    if (IPGeolocationService.#hasHint(this.#pendingHint)) {
      this.#publish(this.#pendingHint);
      return;
    }

    if (IPGeolocationService.#emptyRemembered()) {
      log("Geo proxy already returned an empty location for this tab");
      this.#publishUnknown();
      return;
    }

    const generation = this.#lookupGeneration;
    this.#abortLookup?.abort();
    const abort = new AbortController();
    this.#abortLookup = abort;
    this.#lookupInFlight = true;

    try {
      const response = await this.#fetchProxy(abort.signal);

      if (!this.#lookupIsCurrent(generation)) return;

      if (!this.#preferencesAllowedNow()) {
        if (IPGeolocationService.#explicitDenial()) {
          this.#clearStoredLocation();
        }
        return;
      }

      if (!response.reached) {
        log("Geo proxy returned an unknown location");
        this.#publishUnknown();
        return;
      }

      const hint = IPGeolocationService.#hintFromBody(response.body);

      if (IPGeolocationService.#hasHint(hint)) {
        this.#publish(hint);
        return;
      }

      IPGeolocationService.#rememberEmpty();
      log("Geo proxy returned an unknown location");
      this.#publishUnknown();
    } finally {
      if (this.#lookupGeneration === generation) {
        this.#lookupInFlight = false;
        this.#abortLookup = null;
      }
    }
  }

  /**
   * Preferences consent gates the lookup. Outside a banner region Shopify
   * already allows the purpose, and that decision does not wait for a dialog.
   * A missing Customer Privacy API fails closed: no proxy call. The stored
   * hint is removed only after an explicit denial.
   * @returns {Promise<boolean>}
   */
  async #preferencesAllowed() {
    const shopify = window.Shopify;
    if (IPGeolocationService.#readPreferences() === null) {
      if (!shopify || typeof shopify.loadFeatures !== "function") return false;

      await new Promise((resolve) => {
        shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          () => resolve(undefined),
        );
      });
    }

    if (IPGeolocationService.#readPreferences() === null) return false;
    if (IPGeolocationService.#explicitDenial()) return false;
    if (IPGeolocationService.#explicitAllow()) return true;

    // Only a region Shopify marks as needing a banner waits for the dialog.
    // A non-banner region decides from preferencesProcessingAllowed() immediately.
    if (
      IPGeolocationService.#bannerRequired() &&
      IPGeolocationService.#consentUnset() &&
      !IPGeolocationService.#bannerIsVisible()
    ) {
      await IPGeolocationService.#waitForBanner(2000);
      if (IPGeolocationService.#explicitAllow()) return true;
      return false;
    }

    // preferencesProcessingAllowed() can already be true while the dialog is
    // on screen. That is not Accept.
    if (
      IPGeolocationService.#bannerIsVisible() &&
      !IPGeolocationService.#explicitAllow()
    ) {
      return false;
    }

    return IPGeolocationService.#readPreferences() === true;
  }

  /**
   * @returns {boolean | null} null when the privacy API is missing.
   * False while the privacy dialog is on screen and the visitor has not accepted.
   */
  static #readPreferences() {
    const privacy = window.Shopify?.customerPrivacy;
    if (
      !privacy ||
      typeof privacy.preferencesProcessingAllowed !== "function"
    ) {
      return null;
    }

    if (privacy.preferencesProcessingAllowed() !== true) return false;
    if (IPGeolocationService.#explicitDenial()) return false;
    if (
      IPGeolocationService.#bannerIsVisible() &&
      !IPGeolocationService.#explicitAllow()
    ) {
      return false;
    }

    return true;
  }

  /**
   * @returns {string}
   */
  static #preferenceChoice() {
    const choice =
      window.Shopify?.customerPrivacy?.currentVisitorConsent?.()?.preferences;
    return typeof choice === "string" ? choice : "";
  }

  /**
   * @returns {boolean}
   */
  static #explicitAllow() {
    return IPGeolocationService.#preferenceChoice() === "yes";
  }

  /**
   * @returns {boolean}
   */
  static #explicitDenial() {
    return IPGeolocationService.#preferenceChoice() === "no";
  }

  /**
   * @returns {boolean}
   */
  static #consentUnset() {
    const choice = IPGeolocationService.#preferenceChoice();
    return choice !== "yes" && choice !== "no";
  }

  /**
   * Shopify's signal that this visitor is in a cookie-banner region.
   * @returns {boolean}
   */
  static #bannerRequired() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.shouldShowBanner !== "function") {
      return false;
    }

    return privacy.shouldShowBanner() === true;
  }

  /**
   * @returns {boolean}
   */
  static #bannerIsVisible() {
    const banner = document.querySelector(
      "#shopify-pc__banner, .shopify-pc__banner__dialog",
    );
    if (!banner) return false;

    const style = window.getComputedStyle(banner);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return false;
    }

    const rect = banner.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * @param {number} ms
   * @returns {Promise<boolean>}
   */
  static #waitForBanner(ms) {
    if (IPGeolocationService.#bannerIsVisible()) return Promise.resolve(true);

    return new Promise((resolve) => {
      const finish = (visible) => {
        observer.disconnect();
        clearTimeout(timer);
        resolve(visible);
      };
      const observer = new MutationObserver(() => {
        if (IPGeolocationService.#bannerIsVisible()) finish(true);
      });
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
      });
      const timer = setTimeout(
        () => finish(IPGeolocationService.#bannerIsVisible()),
        ms,
      );
    });
  }

  /**
   * Drop a stored hint when preferences consent is off.
   * Clears settled so a later Accept can publish and waiters will listen again.
   */
  #clearStoredLocation() {
    this.#lookupGeneration += 1;
    this.#abortLookup?.abort();
    this.#abortLookup = null;
    this.#lookupInFlight = false;
    this.#pendingHint = IPGeolocationService.#empty();
    this.#hintCaptured = true;
    IPGeolocationService.#forgetEmpty();
    IPGeolocationService.clearLocationData();
    this.#locationData = IPGeolocationService.#empty();
    this.#locationReceived = false;
    IPGeolocationService.#settled = false;
  }

  /**
   * @param {number} generation
   * @returns {boolean}
   */
  #lookupIsCurrent(generation) {
    return generation === this.#lookupGeneration;
  }

  /**
   * Sync read for the moment a response arrives.
   * A missing API fails closed.
   * @returns {boolean}
   */
  #preferencesAllowedNow() {
    return IPGeolocationService.#readPreferences() === true;
  }

  /**
   * Subscribe once. Accept and a later denial both run through initialize.
   */
  #listenForConsent() {
    if (this.#listeningForConsent) return;

    this.#listeningForConsent = true;
    document.addEventListener("visitorConsentCollected", () => {
      void this.initialize();
    });
  }

  /**
   * A 200 is `reached`, even when the body has no location.
   * A non-OK status, a timeout, and a network error are not stored.
   * @param {AbortSignal} parentSignal
   * @returns {Promise<{ reached: boolean, body: unknown }>}
   */
  async #fetchProxy(parentSignal) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      IPGeolocationService.#TIMEOUT,
    );
    const abortFromParent = () => controller.abort();
    parentSignal?.addEventListener("abort", abortFromParent);

    try {
      const response = await fetch(IPGeolocationService.#GEO_PATH, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "true",
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        log("Geo proxy returned", response.status);
        return { reached: false, body: null };
      }

      return { reached: true, body: await response.json() };
    } catch (error) {
      if (error?.name === "AbortError") {
        if (!parentSignal?.aborted) log("Geo proxy timed out");
      } else {
        log("Geo proxy request failed");
      }

      return { reached: false, body: null };
    } finally {
      clearTimeout(timeoutId);
      parentSignal?.removeEventListener("abort", abortFromParent);
    }
  }

  /**
   * Remember a reached 200 with no location for the rest of this tab.
   * Storage that throws is ignored. This page still publishes unknown.
   */
  static #rememberEmpty() {
    safeSessionStorage.setItem(IPGeolocationService.#EMPTY_KEY, "1");
  }

  /**
   * @returns {boolean} True when this tab already saw an empty 200.
   * False when the flag is missing or storage throws, so the next page may fetch.
   */
  static #emptyRemembered() {
    return safeSessionStorage.getItem(IPGeolocationService.#EMPTY_KEY) === "1";
  }

  /**
   * Drop the empty-200 flag so a later Accept can fetch again.
   */
  static #forgetEmpty() {
    safeSessionStorage.removeItem(IPGeolocationService.#EMPTY_KEY);
  }

  /**
   * @returns {string}
   */
  static #cookieDomain() {
    const hostname = window.location.hostname;

    // A single-label host (a local preview) cannot use the production domain.
    if (hostname.includes("myshopify.com") || !hostname.includes(".")) {
      return hostname;
    }

    return ".getmysa.com";
  }

  /**
   * Remember a hint for this tab and announce it.
   * @param {StoredLocationData} data
   */
  #publish(data) {
    const hint = IPGeolocationService.#normalize(data);
    this.#locationData = hint;
    this.#writeSession(hint);
    this.#locationReceived = true;
    IPGeolocationService.#settled = true;
    this.#logLocationData();
    this.#checkLocaleMismatch();
    this.#dispatchLocationEvent();
  }

  /**
   * Unknown location. Nothing is written into the cookie.
   * The caller remembers an empty 200 before this. A failed request is not remembered.
   */
  #publishUnknown() {
    this.#locationData = IPGeolocationService.#empty();
    IPGeolocationService.#memory = IPGeolocationService.#empty();
    this.#locationReceived = true;
    IPGeolocationService.#settled = true;
    this.#dispatchLocationEvent();
  }

  /**
   * @param {StoredLocationData} hint
   */
  #writeSession(hint) {
    IPGeolocationService.#deleteCookie();

    if (!IPGeolocationService.#hasHint(hint)) {
      IPGeolocationService.#memory = IPGeolocationService.#empty();
      return;
    }

    /** @type {Record<string, string>} */
    const payload = {
      country: hint.country,
      region: hint.region,
    };

    if (hint.prefix) payload.prefix = hint.prefix;

    IPGeolocationService.#memory = { ...hint };
    CookieManager.setSession(
      IPGeolocationService.#COOKIE_NAME,
      encodeURIComponent(JSON.stringify(payload)),
      IPGeolocationService.#cookieDomain(),
    );
  }

  /**
   * @param {StoredLocationData} hint
   */
  #logLocationData() {
    log("Location hint:", {
      country: this.#locationData.country || "N/A",
      region: this.#locationData.region || "N/A",
      prefix: this.#locationData.prefix || "N/A",
    });
  }

  /**
   * Store country and detected country differ. No language redirect is attached.
   */
  #checkLocaleMismatch() {
    const country = this.#locationData.country;
    const shopCountry = window.Shopify?.country;
    if (!country || !shopCountry) return;
    if (shopCountry.toUpperCase() === country) return;

    document.dispatchEvent(
      new LocaleMismatchEvent({
        country,
        locale: window.Shopify?.locale || "",
      }),
    );
  }

  #dispatchLocationEvent() {
    document.dispatchEvent(new LocationLoadedEvent({ ...this.#locationData }));
  }

  /**
   * @param {StoredLocationData} data
   * @returns {boolean}
   */
  static #hasHint(data) {
    return Boolean(data.country || data.region || data.prefix);
  }

  /**
   * A stored cookie still has a full postal, or its prefix is not 3 characters.
   * @param {Object} raw
   * @returns {boolean}
   */
  static #needsRewrite(raw) {
    if (
      raw.zip ||
      raw.postal ||
      raw.country_name ||
      raw.continent ||
      raw.locale ||
      raw.currency_symbol
    ) {
      return true;
    }

    if (typeof raw.prefix !== "string" || !raw.prefix) return false;

    return raw.prefix !== IPGeolocationService.#postalPrefix(raw.prefix);
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string}
   */
  static #isoCountry(value) {
    if (typeof value !== "string") return "";

    const code = value.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : "";
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string}
   */
  static #regionCode(value) {
    if (typeof value !== "string") return "";

    const code = value.trim().toUpperCase();
    return /^[A-Z]{2,3}$/.test(code) ? code : "";
  }

  /**
   * Keep the first 3 postal characters. A longer value is not stored whole.
   * @param {string | null | undefined} value
   * @returns {string}
   */
  static #postalPrefix(value) {
    if (typeof value !== "string") return "";

    const prefix = value
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);

    return prefix.length === 3 ? prefix : "";
  }

  /**
   * Proxy field, then a legacy cookie field.
   * @param {Object} raw
   * @returns {string}
   */
  static #postalSource(raw) {
    if (typeof raw.postal === "string" && raw.postal) return raw.postal;
    if (typeof raw.zip === "string" && raw.zip) return raw.zip;
    if (typeof raw.prefix === "string" && raw.prefix) return raw.prefix;
    return "";
  }

  /**
   * A 200 body that is missing or not an object has no location fields.
   * @param {unknown} body
   * @returns {StoredLocationData}
   */
  static #hintFromBody(body) {
    if (!body || typeof body !== "object") return IPGeolocationService.#empty();

    return IPGeolocationService.#normalize(/** @type {Object} */ (body));
  }

  /**
   * @param {Object} raw
   * @returns {StoredLocationData}
   */
  static #normalize(raw) {
    return {
      country: IPGeolocationService.#isoCountry(raw.country),
      region: IPGeolocationService.#regionCode(raw.region),
      prefix: IPGeolocationService.#postalPrefix(
        IPGeolocationService.#postalSource(raw),
      ),
    };
  }

  /**
   * @returns {Object | null}
   */
  static #readRaw() {
    const cookieValue = CookieManager.get(IPGeolocationService.#COOKIE_NAME);
    if (!cookieValue) return null;

    try {
      const parsed = JSON.parse(decodeURIComponent(cookieValue));
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * @returns {StoredLocationData}
   */
  static #readCookie() {
    const raw = IPGeolocationService.#readRaw();
    if (!raw) return IPGeolocationService.#empty();

    return IPGeolocationService.#normalize(raw);
  }

  static #deleteCookie() {
    const domain = IPGeolocationService.#cookieDomain();
    CookieManager.delete(IPGeolocationService.#COOKIE_NAME, domain);
    CookieManager.delete(IPGeolocationService.#COOKIE_NAME);
  }

  /**
   * True once a lookup has finished, including an unknown result.
   * False while consent is still pending, so callers can keep waiting.
   * @returns {boolean}
   */
  static hasSettled() {
    return IPGeolocationService.#settled;
  }

  /**
   * @returns {StoredLocationData}
   */
  static getLocationData() {
    if (IPGeolocationService.#memory) {
      return { ...IPGeolocationService.#memory };
    }

    return IPGeolocationService.#readCookie();
  }

  /**
   * Clear the session hint and the in-memory copy.
   */
  static clearLocationData() {
    IPGeolocationService.#memory = IPGeolocationService.#empty();
    IPGeolocationService.#deleteCookie();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", async () => {
    if (!window.Shopify?.designMode) {
      const service = new IPGeolocationService();
      await service.initialize();
    }
  });
} else if (!window.Shopify?.designMode) {
  const service = new IPGeolocationService();
  service.initialize();
}

export { IPGeolocationService };
